import React, { useState, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import PurchaseForm from '../../components/Form/PurchaseForm';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { Purchase, TableColumn } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import {
  usePurchases,
  useCreatePurchase,
  useUpdatePurchase,
  useDeletePurchase,
} from '../../hooks/usePurchases';
import { useProviders } from '../../hooks/useProviders';
import { useCategories } from '../../hooks/useCategories';
import { resolveCurrencyCode } from '../../utils/currencyUtils';
import { savePurchaseItems, deleteRemovedPurchaseItems, fetchPurchaseItems } from './purchaseItemsSync';

const Purchases: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState<Purchase | undefined>();

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 },
  }), []);

  const { data: purchases, isLoading, error, refetch } = usePurchases(queryOptions);
  const { data: providers } = useProviders(queryOptions);
  const { data: categories } = useCategories(queryOptions);

  const createMutation = useCreatePurchase(() => {
    setShowForm(false);
    setEditingPurchase(undefined);
    setTimeout(() => refetch(), 100);
  });

  const updateMutation = useUpdatePurchase(() => {
    setShowForm(false);
    setEditingPurchase(undefined);
    setTimeout(() => refetch(), 100);
  });

  const deleteMutation = useDeletePurchase(() => {
    setTimeout(() => refetch(), 100);
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [currencyFilter, setCurrencyFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [amountFromFilter, setAmountFromFilter] = useState<string>('');
  const [amountToFilter, setAmountToFilter] = useState<string>('');

  const getProviderName = useCallback((providerId: string) => {
    if (!providers) return '-';
    const provider = providers.find(
      (p: any) => p.documentId === providerId || p.id?.toString() === providerId?.toString()
    );
    return provider ? provider.name : '-';
  }, [providers]);

  const getCategoryName = useCallback((purchase: Purchase) => {
    if (!categories) return '-';
    // Prefer the category relation when set
    if (purchase.categoryId) {
      const category = categories.find(
        (cat: any) => cat.documentId === purchase.categoryId || cat.id?.toString() === purchase.categoryId?.toString()
      );
      if (category) return category.name;
    }
    // Legacy fallback: guess from the first item's name
    if (!purchase.items || !purchase.items.length) return '-';
    const item = purchase.items[0];
    const category = categories.find(cat => item.name.toLowerCase().includes(cat.name.toLowerCase()));
    return category ? category.name : item.name;
  }, [categories]);

  const filteredPurchases = useMemo(() => {
    if (!purchases) return [];
    return purchases.filter(purchase => {
      const providerName = getProviderName(purchase.providerId);
      const categoryName = getCategoryName(purchase);
      const matchesSearch = !searchTerm || (
        providerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        categoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        purchase.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
        purchase.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (purchase.items || []).some(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      const matchesStatus = statusFilter === 'all' || purchase.status === statusFilter;
      const matchesProvider = providerFilter === 'all' || purchase.providerId?.toString() === providerFilter.toString();
      const purchaseCurrencyCode = resolveCurrencyCode(purchase.currency);
      const matchesCurrency = currencyFilter === 'all' || purchaseCurrencyCode === currencyFilter;
      const matchesCategory = categoryFilter === 'all' || getCategoryName(purchase).toLowerCase().includes(categoryFilter.toLowerCase());
      const orderDate = new Date(purchase.orderDate);
      const matchesDateFrom = !dateFromFilter || orderDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || orderDate <= new Date(dateToFilter);
      const matchesAmountFrom = !amountFromFilter || purchase.total >= parseFloat(amountFromFilter);
      const matchesAmountTo = !amountToFilter || purchase.total <= parseFloat(amountToFilter);
      return matchesSearch && matchesStatus && matchesProvider && matchesCurrency &&
        matchesCategory && matchesDateFrom && matchesDateTo && matchesAmountFrom && matchesAmountTo;
    });
  }, [purchases, searchTerm, statusFilter, providerFilter, currencyFilter, categoryFilter,
    dateFromFilter, dateToFilter, amountFromFilter, amountToFilter, getProviderName, getCategoryName]);

  const handleExport = () => {
    const exportData = filteredPurchases.map(purchase => ({
      'Provider': getProviderName(purchase.providerId),
      'Category': getCategoryName(purchase),
      'Total': purchase.total,
      'Currency': resolveCurrencyCode(purchase.currency),
      'Status': purchase.status,
      'Order Date': new Date(purchase.orderDate).toLocaleDateString(),
      'Notes': purchase.notes || ''
    }));
    exportToExcel(exportData, `purchases_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredPurchases.map(purchase => ({
      'Provider': getProviderName(purchase.providerId),
      'Category': getCategoryName(purchase),
      'Total': purchase.total,
      'Currency': resolveCurrencyCode(purchase.currency),
      'Status': purchase.status,
      'Order Date': new Date(purchase.orderDate).toLocaleDateString(),
      'Notes': purchase.notes || ''
    }));
    printAsExcel(exportData, 'Purchases');
  };

  const filters: FilterConfig[] = [
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [{ value: 'all', label: 'All Statuses' }, { value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'delivered', label: 'Delivered' }] },
    { key: 'provider', label: 'Provider', type: 'select', value: providerFilter, onChange: setProviderFilter, options: [{ value: 'all', label: 'All Providers' }, ...(providers || []).map((p: any) => ({ value: p.documentId || p.id?.toString(), label: p.name }))] },
    { key: 'currency', label: 'Currency', type: 'select', value: currencyFilter, onChange: setCurrencyFilter, options: [{ value: 'all', label: 'All Currencies' }, ...Array.from(new Set((purchases || []).map(p => resolveCurrencyCode(p.currency)))).map(code => ({ value: code, label: code }))] },
    { key: 'category', label: 'Category', type: 'select', value: categoryFilter, onChange: setCategoryFilter, options: [{ value: 'all', label: 'All Categories' }, ...(categories || []).map(c => ({ value: c.name, label: c.name }))] },
    { key: 'dateFrom', label: 'From Date', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'To Date', type: 'date', value: dateToFilter, onChange: setDateToFilter },
    { key: 'amountFrom', label: 'Min Amount', type: 'number', value: amountFromFilter, onChange: setAmountFromFilter, placeholder: '0.00', step: '0.01' },
    { key: 'amountTo', label: 'Max Amount', type: 'number', value: amountToFilter, onChange: setAmountToFilter, placeholder: '999999.99', step: '0.01' },
  ];

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setProviderFilter('all');
    setCurrencyFilter('all');
    setCategoryFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
    setAmountFromFilter('');
    setAmountToFilter('');
  };

  const columns: TableColumn[] = [
    { key: 'providerId', label: 'Provider', sortable: true, render: (value: string) => getProviderName(value) },
    { key: 'category', label: 'Category', sortable: true, render: (_: any, item: Purchase) => getCategoryName(item) },
    { key: 'total', label: 'Total', sortable: true, render: (value: number, row: any) => {
      const currCode = resolveCurrencyCode(row.currency);
      const symbol = currCode === 'USD' ? '$' : 'EGP';
      return `${symbol} ${value.toLocaleString()}`;
    } },
    { key: 'currency', label: 'Currency', sortable: true, render: (value: any) => resolveCurrencyCode(value) },
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value === 'pending' ? 'bg-yellow-100 text-yellow-800' :
        value === 'approved' ? 'bg-blue-100 text-blue-800' :
        'bg-green-100 text-green-800'}`}>{value}
      </span>
    ) },
    { key: 'orderDate', label: 'Order Date', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'deliveryDate', label: 'Delivery Date', sortable: true, render: (value?: string) => value ? new Date(value).toLocaleDateString() : '-' },
    { key: 'notes', label: 'Notes', sortable: false },
  ];

  const handleEdit = async (purchase: Purchase) => {
    let items = purchase.items || [];
    if (purchase.documentId) {
      items = await fetchPurchaseItems(purchase.documentId);
    }
    setEditingPurchase({ ...purchase, items });
    setShowForm(true);
  };

  const handleDelete = async (purchase: Purchase) => {
    if (!window.confirm(`Are you sure you want to delete purchase ${purchase.id}?`)) return;
    if (!purchase?.id) return;
    try {
      await deleteMutation.execute(purchase);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleAdd = () => {
    setEditingPurchase(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<Purchase>) => {
    try {
      // Items live in the separate purchase-item collection: persist them first,
      // then link their documentIds on the purchase via the purchaseItems relation.
      const itemDocumentIds = await savePurchaseItems(data.items || []);
      await deleteRemovedPurchaseItems(editingPurchase?.items, itemDocumentIds);

      const payload: any = {
        providerId: data.providerId,
        categoryId: data.categoryId || null,
        billId: data.billId || null,
        purchaseItems: itemDocumentIds,
        total: data.total || 0,
        currency: typeof data.currency === 'object'
          ? (data.currency as any)?.documentId || (data.currency as any)?.id
          : (data.currency || null),
        exchangeRate: data.exchangeRate,
        status: data.status || 'pending',
        orderDate: data.orderDate,
        deliveryDate: data.deliveryDate,
        notes: data.notes,
      };

      if (editingPurchase?.id) {
        await updateMutation.execute({ id: editingPurchase.id, data: { ...payload, documentId: editingPurchase.documentId } });
      } else {
        await createMutation.execute(payload);
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingPurchase(undefined);
  };

  if (isLoading) return <TableSkeleton />;
  if (error) return <ErrorState message="Error loading purchases. Please try again." onRetry={refetch} entityName="purchases" />;

  if (showForm) {
    return (
      <div className="space-y-6">
        <PurchaseForm purchase={editingPurchase} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Purchases" description="Manage your company purchases and supplier orders" />
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search purchases..."
        filters={filters}
        onClearFilters={handleClearFilters}
        totalCount={purchases?.length ?? 0}
        filteredCount={filteredPurchases.length}
        entityName="purchases"
        onAdd={handleAdd}
        addLabel="Add Purchase"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <Table
        columns={columns}
        data={filteredPurchases}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        title=""
        searchPlaceholder=""
      />
    </div>
  );
};

export default Purchases;
